import { AutoDisableableDto, type AutoDisableableInterface } from '@zaoblako/shared/common/dtos/auto-disableable.dto';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { DeadlineFormDto, type DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import { PlanningUnitFormDto, type PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { TaskKindEnumI18n } from '@zaoblako/shared/modules/task/i18n/task-kind-enum.i18n';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { TaskPaymentTypeEnumI18n } from '@zaoblako/shared/modules/task/i18n/task.payment.type.enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { entityLikeSchema, entityLikeSchemaUuid, entityLikeSchemaUuidStrict } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { BusinessCheckerKeysEnum } from '@zaoblako/shared/modules/business/enum/business-checker-keys.enum';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import type { BudgetClassificationCodeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-classification-code/budget-classification-code-find.dto';
import type { PayerStatusFindInterface } from '@zaoblako/shared/modules/catalog/dtos/payer-status/payer-status-find.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';

import * as yup from 'yup';

/** Task template creation request body - DTO signature */
export interface TaskTemplateFormInterface extends AutoDisableableInterface {
  /** Display name of template. Will not be used by task, created with this template. */
  name: string;
  /** Task subject (display name) */
  subject: string;
  /** Task amount (cannot overflow orders summary amount) */
  amount: number;
  /** Task payment type */
  type: TaskPaymentTypeEnum;
  /** Task kind */
  kind?: TaskKindEnum;
  /** Payment by credit funds */
  creditPayment?: boolean;
  /** Planning settings */
  planning: PlanningUnitFormInterface;
  /** Deadline settings */
  deadline: DeadlineFormInterface;
  /** Tax document date deadline settings */
  documentDate?: DeadlineFormInterface;
  /** Initial status for BRS task */
  status: TaskStatusFindInterface;
  /** Task responsible user */
  responsible?: UserFindInterface;
  /** Approval scheme to use */
  scheme: ApprovalSchemeFindInterface;
  /** Task counterparty: existing or parameters for new */
  contragent: ContragentFindInterface;
  /** Counterparty bank details */
  contragentBankDetails?: BankDetailsFindInterface;
  /** Company which pays */
  organization: CompanyFindInterface;
  /** Organization (payer) bank details */
  organizationBankDetails?: BankDetailsFindInterface;
  /** Corresponding unit */
  unit?: UnitFindInterface;
  /** Corresponding filial */
  filial?: FilialFindInterface;
  /** Corresponding direction */
  direction?: DirectionFindInterface;
  /** Task priority */
  priority: TaskPriorityFindInterface;
  /** Task's VAT (value-added tax) */
  nds: NdsFindInterface;
  /** Users whose are task auditors */
  userAuditors?: UserFindInterface[];
  /** User groups whose members are task auditors */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Corresponding deal */
  deal?: DealFindInterface;
  /** Related contract */
  contract?: ContractFindInterface;
  /** Corresponding cost center */
  costCenter?: CostCenterFindInterface;
  /** Budget classification code, using only when {@link TaskTemplateFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  budgetClassificactionCode?: BudgetClassificationCodeFindInterface;
  /** Municipality classification code (rus. "ОКТМО"), using only when {@link TaskTemplateFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  municipalityClassificationCode?: string;
  /** Payer status, using only when {@link TaskTemplateFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  payerStatus?: PayerStatusFindInterface;
  /** Payment unique ID for 1C ERP databases */
  paymentId?: string;
  /** Unique Accrual Identifier (rus. "УИН"), using only when {@link TaskTemplateFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  uniqueAccrualIdentifier?: string;
  /** Payment reason, using only when {@link TaskTemplateFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  paymentReason?: string;
  /** Tax period, using only when {@link TaskTemplateFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  taxPeriod?: string;
  /** Document number, using only when {@link TaskTemplateFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  documentNumber?: string;
  /** Task currency ("Russian Ruble" by default) */
  currency?: CurrencyFindInterface;
}

const notOnKind = (...kinds: TaskKindEnum[]) => (
  context: SerdeContextInterface<SerdeContextInterface, TaskTemplateFormInterface>,
): boolean => !kinds.includes(context?.value?.kind);

interface SchemaOnKindParams extends Partial<Record<TaskKindEnum, yup.Schema<any>>> {
  /** Default schema */
  byDefault: yup.Schema<any>;
}

const schemaOnKind = (params: SchemaOnKindParams) => (
  context: SerdeContextInterface<SerdeContextInterface, TaskTemplateFormInterface>,
): yup.Schema<any> => params[context?.value?.kind] || params.byDefault;

/** Task template creation request body - DTO schema */
export const TaskTemplateFormDto = SerdeDtoClass.from<AutoDisableableInterface, TaskTemplateFormInterface>(
  AutoDisableableDto,
  TaskDtoKeysEnum.TASK_TEMPLATE_FORM_DTO,
  {
    name: {
      label: 'Имя',
      serializer: yup.string().required(DtoMessageEnum.EMPTY_STRING),
    },
    subject: {
      label: 'Назначение платежа',
      serializer: yup.string().required(DtoMessageEnum.EMPTY_STRING),
    },
    amount: {
      label: 'Сумма',
      serializer: yup.number().positive(DtoMessageEnum.NOT_POSITIVE),
    },
    type: {
      label: 'Тип платежа',
      serializer: commonEnumValidator(TaskPaymentTypeEnum, TaskPaymentTypeEnumI18n),
    },
    kind: {
      optional: true,
      label: 'Вид платежа',
      serializer: commonEnumValidator(TaskKindEnum, TaskKindEnumI18n),
    },
    creditPayment: {
      optional: true,
      label: 'Оплата через кредитные средства',
      serializer: yup.boolean(),
    },
    planning: {
      label: 'Настройки планирования',
      nested: PlanningUnitFormDto,
      serializer: (_, nested) => nested,
    },
    deadline: {
      label: 'Настройки дедлайна',
      nested: DeadlineFormDto,
      serializer: (_, nested) => nested,
    },
    documentDate: {
      optional: (
        context: SerdeContextInterface<SerdeContextDataInterface, TaskTemplateFormInterface>,
      ) => context.value?.kind !== TaskKindEnum.PAYMENT_OF_TAXES,
      label: 'Настройки дедлайна',
      nested: DeadlineFormDto,
      serializer: (
        context: SerdeContextInterface<SerdeContextDataInterface, TaskTemplateFormInterface>,
        nested,
      ): yup.Schema<any> => context.value?.kind === TaskKindEnum.PAYMENT_OF_TAXES
        ? nested
        : yup.mixed(),
    },
    status: {
      label: 'Начальный статус',
      serializer: entityLikeSchema,
    },
    responsible: {
      optional: true,
      label: 'Ответственный',
      serializer: entityLikeSchema,
      checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    },
    scheme: {
      label: 'Схема согласования',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.APPROVAL_SCHEME_ASSERT_EXISTS_BY_DTO],
    },
    contragent: {
      label: 'Контрагент',
      serializer: entityLikeSchema,
      checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
    },
    contragentBankDetails: {
      optional: true,
      label: 'Рассчетный счет плательщика',
      serializer: entityLikeSchema,
      checkers: [ClientCheckerKeysEnum.BANK_DETAILS_ASSERT_EXISTS_BY_DTO],
    },
    organization: {
      label: 'Плательщик',
      serializer: entityLikeSchema,
      checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
    },
    organizationBankDetails: {
      optional: true,
      label: 'Рассчетный счет плательщика',
      serializer: entityLikeSchema,
      checkers: [ClientCheckerKeysEnum.BANK_DETAILS_ASSERT_EXISTS_BY_DTO],
    },
    unit: {
      optional: context => !context?.data?.requiredAnalyticalFeatures?.unitsRequired,
      label: 'БЕ',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
    },
    filial: {
      optional: context => !context?.data?.requiredAnalyticalFeatures?.filialsRequired,
      label: 'Филиал',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
    },
    direction: {
      optional: context => !context?.data?.requiredAnalyticalFeatures?.directionsRequired,
      label: 'БЕ',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
    },
    priority: {
      label: 'Приоритет',
      serializer: entityLikeSchema,
    },
    nds: {
      label: 'Ставка НДС',
      serializer: entityLikeSchema,
    },
    userAuditors: {
      optional: true,
      label: 'Аудиторы',
      serializer: yup.array(entityLikeSchema),
      checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    },
    userGroupAuditors: {
      optional: true,
      label: 'Группы аудиторов',
      serializer: yup.array(entityLikeSchema),
      checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
    },
    deal: {
      optional: true,
      label: 'Сделка',
      serializer: entityLikeSchema,
      checkers: [BusinessCheckerKeysEnum.DEAL_ASSERT_EXISTS_BY_DTO],
    },
    contract: {
      optional: true,
      hidden: false,
      disabled: false,
      label: 'Договор',
      serializer: entityLikeSchema,
    },
    costCenter: {
      optional: true,
      label: 'Центр затрат',
      serializer: entityLikeSchema,
      checkers: [CatalogCheckersEnum.COST_CENTER_ASSERT_EXISTS_BY_DTO],
    },
    budgetClassificactionCode: {
      optional: notOnKind(TaskKindEnum.PAYMENT_OF_TAXES),
      label: 'КБК',
      serializer: schemaOnKind({
        byDefault: entityLikeSchemaUuid,
        [TaskKindEnum.PAYMENT_OF_TAXES]: entityLikeSchemaUuidStrict,
      }),
      checkers: [CatalogCheckersEnum.BUDGET_CLASSIFICATION_CODE_ASSERT_EXISTS_BY_DTO],
    },
    municipalityClassificationCode: {
      optional: true,
      label: 'ОКТМО',
      serializer: yup
        .string()
        .matches(/^(\d{8}|\d{11})$/i, 'Код ОКТМО должен состоять из 8 или 11 цифр'),
    },
    payerStatus: {
      optional: notOnKind(TaskKindEnum.PAYMENT_OF_TAXES),
      label: 'Статус плательщика',
      serializer: schemaOnKind({
        byDefault: entityLikeSchemaUuid,
        [TaskKindEnum.PAYMENT_OF_TAXES]: entityLikeSchemaUuidStrict,
      }),
      checkers: [CatalogCheckersEnum.PAYER_STATUS_ASSERT_EXISTS_BY_DTO],
    },
    paymentId: {
      optional: true,
      label: 'Идентификатор платежа',
      serializer: yup.string(),
    },
    uniqueAccrualIdentifier: {
      optional: true,
      label: 'УИН',
      serializer: yup
        .string()
        .matches(/^(\d{20}|\d{25})$/i, 'УИН должен состояить из 20 или 25 цифр'),
    },
    paymentReason: {
      optional: true,
      label: 'Основание платежа',
      serializer: yup.string(),
    },
    taxPeriod: {
      optional: true,
      label: 'Налоговый период',
      serializer: yup.string(),
    },
    documentNumber: {
      optional: true,
      label: 'Номер документа',
      serializer: yup.string(),
    },
    currency: {
      optional: true,
      label: 'Валюта',
      serializer: entityLikeSchemaUuid,
      checkers: [CatalogCheckersEnum.CURRENCY_ASSERT_EXISTS_BY_DTO],
    },
  },
);
